import { BACKEND_API_BASE_URL } from '../config';

class ClassroomsAPI {
  async getAllClassrooms(authToken = null, subjectId = null, schoolId = null, adminView = false) {
    const headers = {};
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    let url = `${BACKEND_API_BASE_URL}/api/classrooms`;
    const params = [];
    if (subjectId) {
      params.push(`subject_id=${subjectId}`);
    }
    if (schoolId) {
      params.push(`school_id=${schoolId}`);
    }
    if (adminView) {
      params.push(`admin_view=true`);
    }
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    console.log('🔍 ClassroomsAPI: Requesting URL:', url);
    console.log('🔍 ClassroomsAPI: Parameters:', { subjectId, schoolId, adminView });

    const response = await fetch(url, { headers });
    if (!response.ok) {
      throw new Error('Failed to fetch classrooms');
    }
    const data = await response.json();
    
    console.log('🔍 ClassroomsAPI: Response data:', data);
    return data;
  }

  async createClassroom(classroomData, authToken = null) {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${BACKEND_API_BASE_URL}/api/classrooms`, {
      method: 'POST',
      headers,
      body: JSON.stringify(classroomData),
    });
    if (!response.ok) {
      throw new Error('Failed to create classroom');
    }
    return response.json();
  }
    // ✅ Get filtered classrooms by organization, school, category, and subject
    async getFilteredClassrooms({ organization_id, school_id, category_id, subject_id }, authToken = null) {
      const headers = {};
      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      }

      const query = new URLSearchParams();
      if (organization_id) query.append("organization_id", organization_id);
      if (school_id) query.append("school_id", school_id);
      if (category_id) query.append("category_id", category_id);
      if (subject_id) query.append("subject_id", subject_id);

      const url = `${BACKEND_API_BASE_URL}/api/classrooms/filtered?${query.toString()}`;

      const response = await fetch(url, { headers });
      if (!response.ok) {
        throw new Error('Failed to fetch filtered classrooms');
      }
      return response.json();
    }

  async updateClassroom(id, updateData, authToken = null) {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${BACKEND_API_BASE_URL}/api/classrooms/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(updateData),
    });
    if (!response.ok) {
      throw new Error('Failed to update classroom');
    }
    return response.json();
  }

  async getClassroomById(id, authToken = null) {
    const headers = {};
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${BACKEND_API_BASE_URL}/api/classrooms/${id}`, {
      method: 'GET',
      headers,
    });
    if (!response.ok) {
      throw new Error('Failed to fetch classroom');
    }
    return response.json();
  }

  async deleteClassroom(id, authToken = null) {
    const headers = {};
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${BACKEND_API_BASE_URL}/api/classrooms/${id}`, {
      method: 'DELETE',
      headers,
    });
    if (!response.ok) {
      throw new Error('Failed to delete classroom');
    }
    return response.json();
  }

  async getClassroomsBySubject(subjectId, authToken = null) {
    const headers = {};
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${BACKEND_API_BASE_URL}/api/classrooms/by-subject/${subjectId}`, { headers });
    if (!response.ok) {
      throw new Error('Failed to fetch classrooms by subject');
    }
    return response.json();
  }

  // ✅ Get classrooms by school ID for attendance dropdown
  async getClassroomsBySchool(schoolId, authToken = null, adminView = false) {
    const headers = {};
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    let url = `${BACKEND_API_BASE_URL}/api/classrooms/by-school/${schoolId}`;
    if (adminView) {
      url += `?admin_view=true`;
    }

    console.log('🔍 ClassroomsAPI: getClassroomsBySchool URL:', url);

    const response = await fetch(url, { headers });
    if (!response.ok) {
      throw new Error('Failed to fetch classrooms by school');
    }
    return response.json();
  }

  async assignInstructor(classroomId, instructorId, authToken = null) {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${BACKEND_API_BASE_URL}/api/classrooms/${classroomId}/instructor`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ instructor_id: instructorId }),
    });
    if (!response.ok) {
      throw new Error('Failed to assign instructor');
    }
    return response.json();
  }
  
  async removeInstructor(classroomId) {
    const response = await fetch(`${BACKEND_API_BASE_URL}/api/classrooms/${classroomId}/remove-instructor`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error('Failed to remove instructor');
    }
    return response.json();
  }


  // Get the full hierarchy: categories -> subjects -> classrooms
  async getHierarchy(authToken = null, schoolId = null) {
    const headers = {};
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    let url = `${BACKEND_API_BASE_URL}/api/classrooms/hierarchy`;
    if (schoolId) {
      url += `?school_id=${schoolId}`;
    }

    const response = await fetch(url, { headers });
    if (!response.ok) {
      throw new Error('Failed to fetch classroom hierarchy');
    }
    return response.json();
  }

  // ✅ Get classrooms used in manage view: based on org, school, category
  async getManageViewClassrooms({ organization_id, school_id, category_id }, authToken = null) {
    const headers = {};
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const query = new URLSearchParams();
    if (organization_id) query.append("organization_id", organization_id);
    if (school_id) query.append("school_id", school_id);
    if (category_id) query.append("category_id", category_id);

    const url = `${BACKEND_API_BASE_URL}/api/classrooms/manage?${query.toString()}`;

    const response = await fetch(url, { headers });
    if (!response.ok) {
      throw new Error('Failed to fetch classrooms for manage view');
    }
    return response.json();
  }
}

const classroomsAPI = new ClassroomsAPI();
export default classroomsAPI;
